import type { V9CardId } from './v9-source.ts'

// Display copy only. Canonical manuscripts, source offsets and routing keys stay
// in v9-source. Each reviewed override is literal; unknown copy passes through.
type VariantCopy = Partial<Record<V9CardId, Readonly<Record<string, string>>>>

const variantText: VariantCopy = {
  S04: {
    'provider-example': "Your [named team/provider] keeps doing that work. For example, we can flag a lead that hasn't moved forward after a time you choose, such as 24 or 48 hours. The alert shows which lead needs attention, with evidence and suggested next checks where available. Your team handles the follow-up.",
    'owner-example': "We can flag a lead that hasn't moved forward after the time you set and alert you with what we find. You still handle the customer.",
    'local-example': 'For example, a wrong phone number on a Google listing can stop customers getting through. We flag what we find so you can pass it to [named person].',
  },
  S05: {
    system: 'What software do you use to keep track of customers and leads?',
    threshold: 'How long would you want a lead to wait without moving forward before you get an alert?',
  },
  P01: {
    'unknown-function': "I need to check what it does before I can compare it fairly. We can look at [specific difference].",
  },
  O02: {
    'first-pass': 'Of course. Which part are you thinking about?',
    'still-vague': 'Is it what this adds, the price, or whether you trust us?',
    confirm: 'Does that answer what you were thinking about?',
    revisit: 'When would you like to talk about it again?',
  },
  O03: {
    'no-past-failure': 'Things can work well today and still have problems later. We check for [specific monitored condition].',
  },
  O06: {
    'same-scope-discount': "I can't change that monthly price myself. We can keep these checks at [total], or I can ask for a different price. I can't promise it will be approved.",
  },
  O09: {
    'unknown-purpose': 'What do you want [named person] to look at?',
  },
  O11: {
    'change-authority': 'I need to check the connection and permissions before I can tell you what it is allowed to change.',
  },
  O17: {
    'unknown-process': 'What needs to happen before you can approve this?',
  },
  F02: {
    example: "A website can look fine while a new lead's details never reach the team. If we can check that in your setup, what we find gives [person] something specific to look into.",
  },
  F04: {
    // The exact 'We notify you' boundary is also used by the scoped F09 answer.
    scope: 'We check how customers find and contact you, where new leads go, and whether connected leads have not been acted on or have stopped moving forward. We notify you of what we find. Your team handles customer follow-up.',
    'choose-checks': 'Yes. We agree on what to check with you, based on what the plan and your systems support.',
  },
  F07: {
    'unknown-delivery': "I'll check how alerts can reach [named recipients].",
  },
  F08: {
    threshold: 'You choose how long a lead can wait without being worked on or moving forward before it counts for an alert, for example 24 or 48 hours.',
    cadence: "I'll confirm how often the checks run and when alerts can reach you.",
  },
  F09: {
    'customer-followup': 'No. We spot leads that your team has not acted on or that have stopped moving forward, and alert you. Your team follows up with the customer.',
  },
  F10: {
    'no-guarantee': "We can't promise to catch every problem or prevent every missed lead. We report what we find in [agreed checks], with evidence [person] can use.",
  },
  F12: {
    'new-connector': 'Building the connector that links the two systems takes three business days after your IT team gives us the access we need.',
  },
  F13: {
    'unknown-scope': 'What we need access to depends on the checks you choose. Which check are you asking about?',
    'unknown-access': "I'll check the access needed for [named check] before you decide.",
  },
  F14: {
    'database-purpose': 'AvertSignal is not built to keep a copy of all your customer records. It keeps the account details, settings and check information needed to run the service.',
    'unknown-connection-data': 'I need to check which customer information this connection uses and stores.',
  },
  F15: {
    'general-ai': "I'll check the current product details before I answer.",
  },
  F16: {
    'company-verification': 'What would you like to check about the company?',
  },
  F17: {
    replacement: "Only if the same checks and work still get done. Let's compare [named required function] before changing anything.",
  },
  F18: {
    'new-connector': 'Building the connector that links the two systems takes three business days after your IT team gives us the access we need.',
  },
  F19: {
    'trial-application': "You can apply for a 14-day free trial. We'll check whether you qualify and go over the offer with you.",
  },
  F20: {
    'unknown-term': "I'll check [specific term or payment option] before you decide.",
    'terms-review': "Before you decide, let's go over how you pay, how renewal works, and the rules for cancellation and refunds.",
  },
  I02: {
    'qualified-project-offer': 'The work we agreed on is [deliverables] for [one-time total], with [payment structure]. [Exclusions, ownership and timeline]. Would you like to go ahead?',
  },
}

const variantLabels: VariantCopy = {
  S01: { 'local-reason': 'Known local or search concern', 'local-awareness': 'How they check local or search problems', 'unknown-role': 'Find the right person' },
  S03: { awareness: 'How they find out about missed leads' },
  S04: { 'provider-example': 'Their team handles leads', 'owner-example': 'The owner handles leads', 'provider-monitors': 'Their provider already checks', 'local-example': 'Local listing example', 'technical-search-example': 'Search problem example', findings: 'What the alert tells them', founder: 'Why the founder built this' },
  S05: { system: 'Software for the chosen check', threshold: 'How long a lead can wait', quantity: 'Number of locations or systems', approval: 'Who needs to approve' },
  S06: { invitation: 'Ask if they want to go ahead', 'not-today': 'Not today' },
  S07: { quote: 'Price for the agreed checks' },
  P01: { replacement: 'Replacing something they use', 'unknown-function': 'Need to check what it does' },
  P02: { 'reason-unknown': 'What they want the provider to check', duplication: 'Compare with their existing checks', relationship: "Their provider's role" },
  P03: { 'current-use': 'What they use today', comparison: 'Compare the extra check and price' },
  O01: { relevance: 'Clarify why this might help' },
  O02: { 'first-pass': 'What they are thinking about', 'still-vague': 'The concern is still unclear', confirm: 'Check if that answers it', revisit: 'When to talk again' },
  O03: { 'no-past-failure': 'Things have worked so far' },
  O04: { 'unspecified-price': 'What concerns them about the price', 'roi-guarantee': 'No promised dollar return' },
  O05: { 'budget-limit': 'The budget is not clear yet' },
  O06: { 'lower-plan': 'A lower-priced plan', 'required-scope': 'A cheaper plan would lose a needed check', 'same-scope-discount': 'The same checks for less', negotiability: 'Can the price change?' },
  O07: { 'not-today': 'Why they want to wait', 'still-vague': 'The reason is still unclear' },
  O08: { 'supplied-time': 'Use their callback time', 'missing-time': 'Ask for a callback time', 'ambiguous-pause': 'Clarify the pause' },
  O09: { 'known-approver': 'Use the named approver', 'unknown-purpose': "Ask why they want this person's input" },
  O10: { 'more-proof': 'They need more proof', 'unspecified-trust': 'What would help them trust us', confirm: 'Check if that answers it' },
  O11: { 'vague-security': 'Which security concern?', 'change-authority': 'What can the connection change?', confirm: 'Check if that answers it' },
  O12: { 'trial-application': 'Explain how to apply', 'trial-goal': 'What they want to test', confirm: 'Check if that answers it' },
  O13: { 'general-information': 'What information they want' },
  O14: { setup: 'Explain the setup work', 'specific-concern': 'Which part of setup concerns them?', confirm: 'Check if that answers it' },
  O15: { 'checkout-question': 'Why they want to finish later', 'finish-checkout': 'Finish the agreed checkout' },
  O16: { 'small-business': 'Small business fit', 'referral-channel': 'How referred customers contact them' },
  O17: { 'unknown-process': 'Ask about buying requirements', 'requirement-cleared': 'Has the requirement been met?' },
  G01: { 'known-role': 'Ask for the named person', 'unknown-role': 'Who handles this?' },
  G02: { transfer: 'Ask for a transfer' },
  G03: { 'unknown-contact-route': 'How to reach the right person' },
  G04: { 'approved-send': 'Use the approved email route', 'no-send': 'No email permission' },
  F01: { definition: 'What AvertSignal does', 'whole-way': 'How it works' },
  F02: { 'no-fault-found': 'No fault has been found', example: 'A missed lead example' },
  F03: { 'email-unseen': "They haven't seen the email", 'email-seen': 'They saw the email', 'message-identity': 'Who wrote the message?' },
  F04: { scope: 'What we check', 'multiple-locations': 'More than one location' },
  F05: { 'keep-crm': 'Keep their CRM', 'keep-provider': 'Keep their provider', hubspot: 'HubSpot support' },
  F06: { 'monitoring-not-marketing': 'Checking problems, not bringing in leads' },
  F07: { 'missing-recipient': 'Who should get alerts?', 'unknown-delivery': 'Check how alerts can reach them' },
  F08: { threshold: 'How long a lead can wait', cadence: 'How often checks run and alerts arrive', findings: 'What an alert includes', 'unknown-entitlement': 'Check a report or alert detail' },
  F09: { findings: 'What happens when we find a problem?', recheck: 'Checking again after a fix' },
  F10: { 'no-guarantee': 'What we can and cannot promise' },
  F11: { 'approved-example': 'Show an approved example' },
  F12: { 'new-connector': 'Time to build a new connector', 'active-checks': 'Confirm checks and alerts are working' },
  F13: { 'unknown-scope': 'Which check needs access?', 'unknown-access': 'Check the access needed' },
  F14: { 'database-purpose': 'Do we copy the customer database?', 'unknown-connection-data': 'Check what data a connection uses' },
  F15: { 'general-ai': 'Check the product details about AI', 'specific-ai': 'Check a specific use of AI or data' },
  F16: { 'no-reference': 'Customer reference request', 'company-verification': 'What do they want to check?' },
  F17: { replacement: 'Replacing a specific tool' },
  F18: { 'new-connector': 'A new CRM connection' },
  F19: { 'trial-application': 'Apply for a trial', 'application-state': 'Trial application status' },
  F20: { 'unknown-term': 'Check a specific term or payment option', 'terms-review': 'Go over payment and cancellation terms' },
  C01: { 'checkout-choice': 'How they want to check out' },
  C02: { 'guided-pricing': 'Go through the price together', 'payment-reported': 'They say they have paid' },
  C03: { 'known-error': 'Use the reported error', 'unknown-error': 'Ask what happened' },
  C04: { 'verified-purchase': 'Payment has been verified' },
  N01: { 'supplied-time': 'Use the agreed callback time', 'known-dependency': 'The information or approval still needed', 'saved-appointment': 'Use the saved appointment' },
  E02: { 'equivalent-coverage': 'Their existing checks cover the need', 'unsupported-need': 'We cannot support the needed check', 'required-guarantee': 'We cannot give the required guarantee', 'no-wanted-check': 'They do not want an extra check' },
  I02: { 'qualified-project-offer': 'Price for the agreed one-time work' },
}

const cardTitles: Partial<Record<V9CardId, string>> = {
  S01: 'Who we are and why we called', S02: 'Check a relevant fact', S03: 'How they find out about problems',
  S04: 'How this helps your business', S05: 'A few details about your business', S06: 'The recommended plan and price', S07: 'Explain the price',
  P01: 'Could this replace something they use?', P03: 'Compare with their tool or team',
  O01: 'What is the concern?', O03: 'Things work well or are already covered', O04: 'What concerns them about the price?', O05: 'What fits their budget?',
  O06: 'A cheaper plan or a discount', O08: 'Arrange the requested callback', O09: "Someone else's input or approval", O10: 'Trust, references and proof',
  O11: 'Security, data and permissions', O14: 'Setup work and time', O15: 'Finish checkout later', O16: 'Small business, referrals, CRM or website', O17: 'Buying rules, legal review and payment terms',
  G01: 'Find the right person', G02: 'Explain the call and ask for a transfer', G03: 'How to reach the right person', G04: 'No named contact yet',
  F01: 'What is AvertSignal and how does it work?', F02: 'Have we found a problem? What could go wrong?', F03: 'The email and who wrote it', F04: 'What we check and how locations work',
  F05: 'Keep their CRM and provider', F07: 'Who gets alerts and handles them', F08: 'How checks and alerts work', F09: 'Who follows up and fixes problems',
  F10: 'Can we guarantee every problem will be caught?', F12: 'Setup time and checks going live', F14: 'Customer records and stored data', F16: 'Check the company and customer references',
  F18: 'HubSpot, custom CRMs and new connections', F20: 'Payment, renewal, cancellation and refunds',
  C01: 'Checkout for the accepted offer', C02: 'Checkout link and payment status', C03: 'Help with payment or checkout', C04: 'Verified payment and setup',
  N01: 'The next step and what is still needed', E01: 'Stop or do not contact', E02: 'The service does not fit their needs', E03: 'Not going ahead now',
  I01: 'One-time work', I02: 'Price and details for approved one-time work',
}

const purposeLabels: Readonly<Record<string, string>> = {
  'OBJ-NOT-INTERESTED': 'Not interested', 'OBJ-REFUSAL': 'Stop or do not contact', 'OBJ-READY': "I'd like to buy",
  'OBJ-IMPLEMENTATION': 'One-time work or fixing a problem', '@callback': 'Call me later',
  'OBJ-CALL-EARLY': 'Call me later', 'OBJ-CALL-MID': 'Call me later', 'OBJ-CALL-CLOSE': 'Call me later',
  'OBJ-AUTH': "I need someone else's input or approval", 'OBJ-TRUST': 'I need more proof',
  'OBJ-SECURITY': 'Questions about security or privacy', 'OBJ-REFERENCES': 'Can I see references or proof?',
  'OBJ-COMPETITOR': 'Another tool or provider', 'OBJ-TOO-SMALL': 'Is this useful for a small business?', 'OBJ-TOO-BIG': 'Our team already handles this',
  'OBJ-REFERRALS': 'Most customers come through referrals', 'OBJ-SETUP': 'How much setup work is involved?',
  'OBJ-PROCUREMENT': 'Buying rules or legal review', 'OBJ-INVOICE-PO': 'Invoice, purchase order or supplier setup',
  'OBJ-CHECKOUT-LATER': "I'll finish checkout later", 'OBJ-PAYMENT': 'A problem with payment or checkout',
  'OBJ-PAYMENT-CARD': 'Card problem', 'OBJ-PAYMENT-BANK': 'Bank payment problem', 'OBJ-PAYMENT-PAGE': 'Page or link problem',
}

export function plainVariantText(cardId: V9CardId, variantId: string, original: string): string {
  const copy = Object.hasOwn(variantText, cardId) ? variantText[cardId] : undefined
  return copy && Object.hasOwn(copy, variantId) ? copy[variantId] : original
}

export function plainVariantLabel(cardId: V9CardId, variantId: string, original: string): string {
  const copy = Object.hasOwn(variantLabels, cardId) ? variantLabels[cardId] : undefined
  return copy && Object.hasOwn(copy, variantId) ? copy[variantId] : original
}

export function plainCardTitle(cardId: V9CardId, original: string): string {
  return Object.hasOwn(cardTitles, cardId) ? cardTitles[cardId]! : original
}

export function plainPurposeLabel(purposeId: string, original: string): string {
  return Object.hasOwn(purposeLabels, purposeId) ? purposeLabels[purposeId] : original
}
